/**
 * Besucher-ID fuer die Zuteilung anonymer Besucher (Funnel, LP).
 *
 * Persistent nur mit Consent fuer `performance`. Ohne Consent lebt die ID nur
 * im Speicher dieses Seitenaufrufs, auf dem Geraet wird nichts abgelegt. Ein
 * wiederkehrender Besucher ohne Consent kann dann eine andere Variante sehen.
 * Das verwischt den Effekt leicht Richtung null, erzeugt aber keinen falschen
 * Gewinner.
 *
 * `?vid=` von der Landingpage hat Vorrang, damit LP und Funnel-iframe dieselbe
 * Gruppe sehen. Die LP haelt die ID dann selbst, der Funnel speichert sie
 * nicht zusaetzlich.
 */
import { hasConsentFor } from '../../helpers'

const STORAGE_KEY = 'aivy_vid'
const VID_PATTERN = /^[a-zA-Z0-9-]{8,64}$/

let memoryId = null
let fromQuery = false

const randomId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }
  return `v-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

const readQueryVid = () => {
  try {
    const vid = new URLSearchParams(window.location.search).get('vid')
    return vid && VID_PATTERN.test(vid) ? vid : null
  } catch {
    return null
  }
}

const readStored = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

const persist = (id) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // localStorage gesperrt (Safari privat, iframe ohne Storage-Zugriff)
  }
}

const forget = () => {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch {
    // nichts zu loeschen, wenn localStorage gesperrt ist
  }
}

export const hasPersistentConsent = () => hasConsentFor('performance')

export const getVisitorId = () => {
  if (memoryId) {
    // Consent kann waehrend des Besuchs kommen oder gehen. Kommt er, die
    // bisherige ID behalten und ab jetzt merken, damit die Gruppe gleich
    // bleibt. Geht er, die gespeicherte ID loeschen. Bis zum Reload bleibt
    // sie nur im Speicher.
    if (!fromQuery) {
      if (hasPersistentConsent()) {
        if (!readStored()) persist(memoryId)
      } else {
        forget()
      }
    }
    return memoryId
  }

  const queryVid = readQueryVid()
  if (queryVid) {
    memoryId = queryVid
    fromQuery = true
    return memoryId
  }

  const consent = hasPersistentConsent()
  // Ohne Consent darf nichts auf dem Geraet liegen, auch keine ID von frueher
  if (!consent) forget()
  const stored = consent ? readStored() : null

  memoryId = stored || randomId()
  if (consent && !stored) persist(memoryId)

  return memoryId
}

export const isVisitorIdFromLandingpage = () => {
  getVisitorId()
  return fromQuery
}

// Nur fuer Tests
export const resetVisitorIdForTests = () => {
  memoryId = null
  fromQuery = false
}
